// Strips short-lived Stytch credentials out of PostHog analytics for the
// /identity/* routes, without changing what any other route reports.
//
// Why this exists: Stytch redirects to /identity/callback with a single-use
// discovery token in the query string (?stytch_token_type=discovery_oauth&
// token=...), and posthog-js autocaptures $pageview with $current_url set
// verbatim from window.location.href — so the token would otherwise land in
// PostHog. The same is true of the OAuth request /identity/authorize
// receives (client_id, redirect_uri, scope, state, PKCE code_challenge,
// resource) and of whatever /identity/login is asked to carry, including a
// sibling branch's `origin_assertion`.
//
// The fix is deliberately NOT "strip all query strings everywhere" — this
// business reads UTM parameters off $current_url for attribution, and a
// global strip would silently break that. Only a URL whose PATHNAME starts
// with `/identity/` is reduced (to origin + pathname; query string and hash
// both dropped). Every URL-shaped value on every other route passes through
// unchanged, regardless of which property holds it. See
// auger-web/tests/unit/init/posthogSanitize.test.ts, which pins a
// UTM-preserving case as a regression test for exactly this trade-off.
//
// posthog-js (installed: 1.407.6) hands `before_send` the whole captured
// event, not a flat properties bag: `properties`, plus sibling `$set` /
// `$set_once` objects used for person/session first-touch attribution (e.g.
// `$set_once.$session_entry_url`, `properties.$referrer`). The walk below is
// intentionally generic and recursive rather than a fixed list of keys: any
// string value that parses as an absolute URL is checked against its OWN
// pathname, at any nesting depth, so a property PostHog adds later (or
// nests inside $set/$set_once) is covered without this file needing an
// update. Only string VALUES are ever rewritten — keys are never added or
// removed, which matters because posthog-js drops an event outright if
// `before_send` deletes certain properties it requires for ingestion.
//
// SESSION REPLAY: this file covers ordinary event capture only —
// `before_send`/`sanitize_properties` never touch session replay's separate
// rrweb pipeline. That was flagged here as an open question because this
// repo alone couldn't say whether replay was even on; a human has since
// confirmed on the PostHog dashboard that it IS enabled for this project.
// That gap is now closed in posthogSessionRecording.ts (three layers: don't
// record at all on /identity/*, best-effort URL masking, and a redacted
// network request for the one endpoint that could carry a live credential)
// and wired into posthog.ts's `posthog.init` call — see both files.
//
// NOTE: `sanitize_properties` (the older PostHog config option this fix was
// originally scoped around) is deprecated in the installed posthog-js
// version and logs a console error on every single capture call when used
// (see the deprecation string in posthog-js/dist/array.js). `before_send` is
// the supported replacement, so it is used here instead — do not "simplify"
// this back to `sanitize_properties`.

import type { BeforeSendFn, CaptureResult } from "posthog-js"

// Exported so posthogSessionRecording.ts (the session-replay layer of this
// fix) reuses this exact definition of "identity route" rather than
// introducing a second one — the init-time disable, the router-driven
// stop/start guard, and this URL sanitizer must never be able to drift
// apart on what counts as /identity/*.
export const IDENTITY_PATH_PREFIX = "/identity/"
// Properties objects are a few levels deep at most (e.g. $set_once holding
// plain string/number values); this just stops a pathological input from
// recursing forever rather than reflecting any real PostHog shape.
const MAX_SANITIZE_DEPTH = 4

/**
 * If `value` is a URL string whose pathname is under /identity/, returns
 * origin + pathname only — query string and hash both dropped. Otherwise
 * returns `value` unchanged. Never throws: a non-string, an empty string, or
 * a string that doesn't parse as an absolute URL all pass through as-is.
 */
export function sanitizeIdentityUrl(value: unknown): unknown {
  if (typeof value !== "string" || value.length === 0) return value
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return value
  }
  if (!url.pathname.startsWith(IDENTITY_PATH_PREFIX)) return value
  return `${url.origin}${url.pathname}`
}

function sanitizeValue(value: unknown, depth: number): unknown {
  if (typeof value === "string") return sanitizeIdentityUrl(value)
  if (value === null || typeof value !== "object" || depth >= MAX_SANITIZE_DEPTH) return value
  try {
    if (Array.isArray(value)) {
      return value.map((item) => sanitizeValue(item, depth + 1))
    }
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(value as Record<string, unknown>)) {
      out[key] = sanitizeValue((value as Record<string, unknown>)[key], depth + 1)
    }
    return out
  } catch {
    // A property object that isn't plain-enumerable (a Proxy that throws on
    // Object.keys, etc.) degrades to "leave it alone" rather than breaking
    // the capture call.
    return value
  }
}

/**
 * Recursively sanitizes every URL-shaped string in a PostHog properties (or
 * $set / $set_once) object. Property KEYS are always preserved; only values
 * are ever rewritten, and only when they are strings that parse as an
 * identity URL. Never throws — any failure returns `properties` unchanged.
 */
export function sanitizeIdentityProperties<T>(properties: T): T {
  try {
    return sanitizeValue(properties, 0) as T
  } catch {
    return properties
  }
}

/**
 * The `before_send` hook passed to posthog.init. Sanitizes `properties`,
 * `$set`, and `$set_once` on the captured event and leaves everything else
 * (event name, uuid, timestamp, ...) untouched. Never throws and never drops
 * an event on its own — a failure here degrades to "send the event
 * unsanitized" rather than losing analytics or breaking the page.
 */
export const sanitizeIdentityBeforeSend: BeforeSendFn = (event) => {
  if (!event) return event
  try {
    const sanitized: CaptureResult = {
      ...event,
      properties: sanitizeIdentityProperties(event.properties),
    }
    if (event.$set !== undefined) sanitized.$set = sanitizeIdentityProperties(event.$set)
    if (event.$set_once !== undefined) {
      sanitized.$set_once = sanitizeIdentityProperties(event.$set_once)
    }
    return sanitized
  } catch {
    return event
  }
}
